/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { useGlobalFiltersStore } from '@/stores/global-filters.store';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';
import {
    fetchSubscriptionPageRules,
    fetchSuggestedPageRulesHandledList,
    filterOutPages,
    removeSelectedPageRules,
    updateSuggestedPageRulesHandledList,
    canCreatePages,
    callPageLoadEventsAgg,
    callAiSuggestedPagesAgg,
    convertSubscriptionPageRules,
    generateSuggestedPageRules,
} from '@/vuex/modules/pages/suggested-pages/suggested-pages.utils';

import { useSubscriptionStore } from '@/stores/subscription.store';
import { useAppsStore } from '@/stores/apps.store';

interface SuggestedPagesState {
    pageLoadEvents: unknown[];
    subscriptionPageRules: unknown[];
    suggestedPageRulesHandledList: unknown[];
    suggestedPageRules: unknown[];
    suggestedPageRulesFilteredData: unknown[];
    error: unknown;
}

function getInitialState (): SuggestedPagesState {
    return {
        pageLoadEvents: [],
        subscriptionPageRules: [],
        suggestedPageRulesHandledList: [],
        suggestedPageRules: [],
        suggestedPageRulesFilteredData: [],
        error: null,
    };
}

export const useSuggestedPagesStore = defineStore('suggestedPages', {
    state: getInitialState,
    getters: {
        isAiSuggestionsEnabled (): boolean {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when auth migrates to Pinia
            const subscription = useSubscriptionStore().getActiveSub;
            const hasSegmentFlag = vuexStore?.getters['auth/hasSegmentFlag'];

            return (
                get(subscription, 'featureFlags.enableMlSuggestedCountables', false) &&
                hasSegmentFlag('pagesAutoTagging')
            );
        },
    },
    actions: {
        async hydrate () {
            await this.fetchPageLoadEvents();
            await this.fetchSubscriptionPageRules();
            await this.fetchSuggestedPageRulesHandledList();
            await this.createSuggestedPageRules();
        },
        async fetchPageLoadEvents () {
            this.error = null;
            const userPreferences = useUserSettingsStore().crossUISettingByName('userPreferences');
            const suggestedPagesTableEnabled = get(userPreferences, 'value.suggestedPagesTable.value', false);
            if (!suggestedPagesTableEnabled) {
                this.pageLoadEvents = [];

                return [];
            }
            const allApps = useAppsStore().map;
            const apps = canCreatePages(allApps);
            if (isEmpty(apps)) {
                this.pageLoadEvents = [];

                return [];
            }
            const appId = Object.entries(apps).reduce((acc: string[], app: [string, any]) => {
                if (app[1].applicationFlags.suggestPageTags) {
                    acc.push(app[0]);
                }

                return acc;
            }, []);
            if (isEmpty(appId)) {
                this.pageLoadEvents = [];

                return [];
            }
            let sampleDenominator = 1;
            Object.values(apps).map((app: any) => {
                if (!isNil(app.eventFilePseudoShardCount) && app.eventFilePseudoShardCount !== 0) {
                    sampleDenominator = app.eventFilePseudoShardCount;
                }

                return sampleDenominator;
            });
            const eventFileShards = get(useSubscriptionStore().getActiveSub, 'eventFileShards', 0);
            sampleDenominator = Math.max(sampleDenominator, eventFileShards);
            const eventRate = get(useSubscriptionStore().getActiveSub, 'eventRate', 1);
            const MAX_EVENTS_PER_HOUR = 2500000;
            const MAX_HOUR_COUNT = 164;
            const count = Math.min(MAX_HOUR_COUNT, Math.max(Math.floor(MAX_EVENTS_PER_HOUR / eventRate), 1)) * -1;
            const aggParams = {
                appId,
                blacklist: 'apply',
                timeSeries: {
                    first: -1,
                    period: 'hourRange',
                    count,
                },
                sampleDenominator,
                includeAnonymous: true,
            };

            let pageLoadEvents: unknown[] = [];
            try {
                if (this.isAiSuggestionsEnabled) {
                    pageLoadEvents = await callAiSuggestedPagesAgg(aggParams);
                } else {
                    pageLoadEvents = await callPageLoadEventsAgg(aggParams);
                }
            } catch (error) {
                this.error = error;
            }

            this.pageLoadEvents = pageLoadEvents;
            this.createSuggestedPageRules();

            return pageLoadEvents;
        },
        async createSuggestedPageRules () {
            const subId = useSubscriptionStore().active.id;

            if (isEmpty(subId)) {
                this.suggestedPageRules = [];
                this.suggestedPageRulesFilteredData = [];

                return [];
            }
            const pageLoadEvents = get(this, 'pageLoadEvents', []);
            if (isEmpty(pageLoadEvents)) {
                this.suggestedPageRules = [];
                this.suggestedPageRulesFilteredData = [];

                return [];
            }

            const subscription = useSubscriptionStore().map[String(subId)];
            if (isEmpty(subscription)) {
                this.suggestedPageRules = [];
                this.suggestedPageRulesFilteredData = [];

                return [];
            }

            const applications = useAppsStore().map;
            if (isEmpty(applications)) {
                this.suggestedPageRules = [];
                this.suggestedPageRulesFilteredData = [];

                return [];
            }
            const suggestedPageRulesHandledList = get(this, 'suggestedPageRulesHandledList', []);

            const { isAiSuggestionsEnabled } = this;
            const suggestedPageRules = (pageLoadEvents as any[]).reduce((acc: unknown[], event: any) => {
                const matchedApplication = Object.values(applications).find((app: any) => app.id === event.appId);
                const displayName = get(matchedApplication, 'displayName', '');

                let sampleCount;
                if (isAiSuggestionsEnabled && Array.isArray(event.rules)) {
                    sampleCount = 5;
                    event = {
                        ...event,
                        name: event.name,
                        rule: [...event.rules].sort().join(', '),
                        isAiSuggestedResults: true,
                    };
                } else {
                    const { rule, name, sampleCount: generatedSampleCount } = generateSuggestedPageRules(event);
                    sampleCount = generatedSampleCount;
                    event = {
                        ...event,
                        rule,
                        name,
                    };
                }

                event = {
                    ...event,
                    id: uuid(),
                    samples: event.samples.slice(0, sampleCount),
                    application: displayName,
                };
                const ruleHandled = (suggestedPageRulesHandledList as any[]).find(
                    (app: any) => app.appId === event.appId && app.rule === event.rule,
                );
                if (ruleHandled) return acc;

                acc.push(event);

                return acc;
            }, []);

            this.suggestedPageRules = suggestedPageRules;
            this.createSuggestedPageRulesFilteredData();

            return suggestedPageRules;
        },
        createSuggestedPageRulesFilteredData () {
            const appRules = get(this, 'suggestedPageRules', {});
            if (isEmpty(appRules)) {
                this.suggestedPageRulesFilteredData = [];

                return [];
            }

            let selectedFilter = useGlobalFiltersStore().nonAggAppId;
            if (!selectedFilter) {
                selectedFilter = ['*'];
            }

            const filteredPageRules = filterOutPages(selectedFilter, Object.values(appRules as object), {
                apps: { map: useAppsStore().map },
            });

            this.suggestedPageRulesFilteredData = filteredPageRules;

            return filteredPageRules;
        },
        async fetchSubscriptionPageRules () {
            this.error = null;
            let subscriptionPageRules: unknown[] = [];

            const userPreferences = useUserSettingsStore().crossUISettingByName('userPreferences');
            const suggestedPagesTableEnabled = get(userPreferences, 'value.suggestedPagesTable.value', false);

            if (!suggestedPagesTableEnabled) {
                return subscriptionPageRules;
            }

            try {
                subscriptionPageRules = await fetchSubscriptionPageRules();
            } catch (error) {
                this.error = error;
            }
            subscriptionPageRules = convertSubscriptionPageRules(subscriptionPageRules);
            this.subscriptionPageRules = subscriptionPageRules;

            return subscriptionPageRules;
        },
        async fetchSuggestedPageRulesHandledList () {
            this.error = null;
            let suggestedPageRulesHandledList: unknown[] = [];

            try {
                suggestedPageRulesHandledList = await fetchSuggestedPageRulesHandledList();
            } catch (error) {
                this.error = error;
            }
            this.suggestedPageRulesHandledList = suggestedPageRulesHandledList;

            return suggestedPageRulesHandledList;
        },
        async updateAndRefreshTable ({ selections, action }: { selections: unknown; action: unknown }) {
            await updateSuggestedPageRulesHandledList({ selections, action });
            this.fetchSuggestedPageRulesHandledList();
            const suggestedPageRulesFilteredData = get(this, 'suggestedPageRulesFilteredData', []);
            const updatedFilteredData = removeSelectedPageRules(selections, suggestedPageRulesFilteredData);
            this.suggestedPageRulesFilteredData = updatedFilteredData;
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSuggestedPagesStore, module.hot));
}
